import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, adminHeaders } from "@/lib/api";
import CampaignDashboardView from "@/pages/client/CampaignDashboardView";

function useNoindex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
}

const CP = `${API}/admin/cp`;

// Dates en français : "2026-01-01" -> "1 janv. 2026"
function frDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" }).format(d);
}

// Heure locale FR "HH:MM" pour l'affichage du verrouillage.
function frTime(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(d);
}

// Un compte est verrouillé si locked_until est dans le futur.
function isLocked(acc) {
  if (!acc || !acc.locked_until) return false;
  const d = new Date(acc.locked_until);
  return !isNaN(d.getTime()) && d.getTime() > Date.now();
}

const STATUS_FR = { invited: "invité", active: "actif", disabled: "désactivé" };

async function cpGet(path) {
  const r = await fetch(`${CP}${path}`, { headers: adminHeaders() });
  if (r.status === 401) { localStorage.removeItem("sliz_admin_token"); window.location.replace("/admin"); return []; }
  return r.ok ? r.json() : [];
}
async function cpPost(path, body) {
  const r = await fetch(`${CP}${path}`, {
    method: "POST", headers: adminHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) { localStorage.removeItem("sliz_admin_token"); window.location.replace("/admin"); return { ok: false, status: 401, data: {} }; }
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, data };
}
async function cpPut(path, body) {
  const r = await fetch(`${CP}${path}`, {
    method: "PUT", headers: adminHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) { localStorage.removeItem("sliz_admin_token"); window.location.replace("/admin"); return { ok: false, status: 401, data: {} }; }
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, data };
}
async function cpDelete(path) {
  const r = await fetch(`${CP}${path}`, { method: "DELETE", headers: adminHeaders() });
  if (r.status === 401) { localStorage.removeItem("sliz_admin_token"); window.location.replace("/admin"); return { ok: false, status: 401 }; }
  return { ok: r.ok, status: r.status };
}

const STATUS_LABELS = ["En préparation", "En production créative", "En production", "En diffusion", "Terminée"];

function Section({ title, testid, children }) {
  return (
    <section style={{ background: "#fff", border: "1px solid var(--li)", padding: 24, marginBottom: 24 }} data-testid={testid}>
      <p className="t-label" style={{ margin: "0 0 16px" }}>{title}</p>
      {children}
    </section>
  );
}

const EDIT_INPUT = { fontFamily: "Archivo", fontSize: 14, padding: "8px 10px", border: "1px solid var(--li)", width: "100%" };

function CampaignEditor({ campaignId, flash }) {
  const [dash, setDash] = useState(null);
  const load = async () => {
    const d = await cpGet(`/campaigns/${campaignId}/dashboard`);
    if (d && d.channels) d.channels.forEach((ch) => { ch.kpis = [0, 1, 2].map((i) => (ch.kpis && ch.kpis[i]) || { label: "", value: "" }); });
    setDash(d);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  const [docs, setDocs] = useState([]);
  const [docForm, setDocForm] = useState({ kind: "brief", title: "", meta: "", published: false });
  const [upPct, setUpPct] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const loadDocs = async () => setDocs(await cpGet(`/campaigns/${campaignId}/documents`));
  useEffect(() => { loadDocs(); /* eslint-disable-next-line */ }, [campaignId]);

  const uploadDoc = async (file) => {
    if (!file) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) { flash("PDF uniquement pour cette étape."); return; }
    if (file.size === 0) { flash("Fichier vide."); return; }
    if (file.size > 25 * 1024 * 1024) { flash("Fichier trop volumineux (25 Mo max)."); return; }
    setUpPct(0);
    try {
      const init = await cpPost(`/campaigns/${campaignId}/documents/upload/init`, { filename: file.name, content_type: file.type || "application/pdf" });
      if (!init.ok) throw new Error(init.data.detail || "Init impossible");
      const { upload_id } = init.data;
      const CHUNK = 4 * 1024 * 1024;
      for (let off = 0; off < file.size; off += CHUNK) {
        const fd = new FormData();
        fd.append("chunk", file.slice(off, off + CHUNK));
        const r = await fetch(`${CP}/documents/upload/${upload_id}/chunk`, { method: "POST", headers: { Authorization: adminHeaders().Authorization }, body: fd });
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || "Envoi interrompu"); }
        setUpPct(Math.min(99, Math.round(((off + CHUNK) / file.size) * 100)));
      }
      const done = await cpPost(`/documents/upload/${upload_id}/complete`, { kind: docForm.kind, title: docForm.title, meta: docForm.meta, published: docForm.published });
      if (!done.ok) throw new Error(done.data.detail || "Finalisation impossible");
      setUpPct(null);
      setDocForm({ kind: "brief", title: "", meta: "", published: false });
      flash("Document ajouté");
      loadDocs();
    } catch (e) { setUpPct(null); flash("Erreur : " + e.message); }
  };
  const setDoc = (id, f, v) => setDocs((list) => list.map((x) => x.id === id ? { ...x, [f]: v } : x));
  const saveDocMeta = async (doc) => { const r = await cpPut(`/documents/${doc.id}`, { kind: doc.kind, title: doc.title, meta: doc.meta }); flash(r.ok ? "Document mis à jour" : (r.data.detail || "Erreur")); if (r.ok) loadDocs(); };
  const togglePublish = async (doc) => { const r = await cpPut(`/documents/${doc.id}`, { published: !doc.published }); flash(r.ok ? (doc.published ? "Dépublié" : "Publié") : (r.data.detail || "Erreur")); if (r.ok) loadDocs(); };
  const delDoc = async (doc) => { if (!window.confirm("Supprimer ce document ?")) return; const r = await cpDelete(`/documents/${doc.id}`); flash(r.ok ? "Document supprimé" : "Erreur"); if (r.ok) loadDocs(); };
  const humanSize = (n) => n >= 1048576 ? (n / 1048576).toFixed(1) + " Mo" : Math.ceil((n || 0) / 1024) + " Ko";

  if (!dash || !dash.campaign) return <p className="t-body" data-testid="cpadmin-editor-loading">Chargement…</p>;
  const c = dash.campaign;
  const setC = (f, v) => setDash((d) => ({ ...d, campaign: { ...d.campaign, [f]: v } }));
  const setMs = (id, f, v) => setDash((d) => ({ ...d, milestones: d.milestones.map((m) => m.id === id ? { ...m, [f]: v } : m) }));
  const setDl = (id, f, v) => setDash((d) => ({ ...d, deliverables: d.deliverables.map((x) => x.id === id ? { ...x, [f]: v } : x) }));
  const setCh = (id, f, v) => setDash((d) => ({ ...d, channels: d.channels.map((x) => x.id === id ? { ...x, [f]: v } : x) }));
  const setKpi = (id, i, f, v) => setDash((d) => ({ ...d, channels: d.channels.map((x) => x.id === id ? { ...x, kpis: x.kpis.map((k, j) => j === i ? { ...k, [f]: v } : k) } : x) }));

  const saveCamp = async () => { const r = await cpPut(`/campaigns/${campaignId}`, { name: c.name, advertiser_label: c.advertiser_label, start_date: c.start_date, end_date: c.end_date, owner_name: c.owner_name, formats: c.formats, diffusion_start: c.diffusion_start }); flash(r.ok ? "Campagne enregistrée" : (r.data.detail || "Erreur")); if (r.ok) load(); };
  const saveMs = async (m) => { const r = await cpPut(`/milestones/${m.id}`, { title: m.title, date: m.date, state: m.state, order: Number(m.order) || 0, phase: m.phase }); flash(r.ok ? "Jalon enregistré" : (r.data.detail || "Erreur")); if (r.ok) load(); };
  const delMs = async (id) => { if (!window.confirm("Supprimer ce jalon ?")) return; const r = await cpDelete(`/milestones/${id}`); flash(r.ok ? "Jalon supprimé" : "Erreur"); if (r.ok) load(); };
  const addMs = async (phase) => { const r = await cpPost(`/campaigns/${campaignId}/milestones`, { phase, title: "Nouveau jalon", state: "todo", order: 99, date: "" }); flash(r.ok ? "Jalon ajouté" : "Erreur"); if (r.ok) load(); };
  const saveDl = async (x) => { const r = await cpPut(`/deliverables/${x.id}`, { status_label: x.status_label, version_label: x.version_label }); flash(r.ok ? "Livrable enregistré" : "Erreur"); if (r.ok) load(); };
  const addCh = async () => { const r = await cpPost(`/campaigns/${campaignId}/channels`, { channel_name: "Nouveau canal", kpis: [{ label: "", value: "" }, { label: "", value: "" }, { label: "", value: "" }] }); flash(r.ok ? "Canal ajouté" : "Erreur"); if (r.ok) load(); };
  const saveCh = async (x) => { const r = await cpPut(`/channels/${x.id}`, { channel_name: x.channel_name, kpis: x.kpis, budget_eur: Number(x.budget_eur) || 0, estimate_label: x.estimate_label }); flash(r.ok ? "Canal enregistré" : "Erreur"); if (r.ok) load(); };
  const delCh = async (id) => { if (!window.confirm("Supprimer ce canal ?")) return; const r = await cpDelete(`/channels/${id}`); flash(r.ok ? "Canal supprimé" : "Erreur"); if (r.ok) load(); };

  const msRow = (m) => (
    <div key={m.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto auto", gap: 8, alignItems: "center", padding: "6px 0" }} data-testid={`cpadmin-ms-${m.id}`}>
      <input style={EDIT_INPUT} value={m.title} onChange={(e) => setMs(m.id, "title", e.target.value)} data-testid={`cpadmin-ms-title-${m.id}`} />
      <input style={EDIT_INPUT} placeholder="AAAA-MM-JJ" value={m.date || ""} onChange={(e) => setMs(m.id, "date", e.target.value)} />
      <select style={EDIT_INPUT} value={m.state} onChange={(e) => setMs(m.id, "state", e.target.value)} data-testid={`cpadmin-ms-state-${m.id}`}>
        <option value="done">Fait</option><option value="now">En cours</option><option value="todo">À venir</option>
      </select>
      <button className="btn" style={{ padding: "5px 8px" }} onClick={() => saveMs(m)} data-testid={`cpadmin-ms-save-${m.id}`}>OK</button>
      <button className="btn" style={{ padding: "5px 8px", color: "#8C1D18" }} onClick={() => delMs(m.id)} data-testid={`cpadmin-ms-del-${m.id}`}>×</button>
    </div>
  );

  return (
    <div data-testid="cpadmin-editor">
      <Section title="Campagne" testid="cpadmin-edit-campaign">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <input style={EDIT_INPUT} value={c.name || ""} onChange={(e) => setC("name", e.target.value)} data-testid="cpadmin-edit-name" />
          <div style={{ ...EDIT_INPUT, background: "#F2F3F5", display: "flex", alignItems: "center", gap: 8 }} data-testid="cpadmin-edit-status">
            <span style={{ fontWeight: 700 }}>{c.status_label || "—"}</span>
            <span style={{ fontSize: 11, color: "var(--roi)" }}>(auto · selon l'étape en cours)</span>
          </div>
          <input style={EDIT_INPUT} placeholder="Annonceur" value={c.advertiser_label || ""} onChange={(e) => setC("advertiser_label", e.target.value)} />
          <input style={EDIT_INPUT} placeholder="Responsable" value={c.owner_name || ""} onChange={(e) => setC("owner_name", e.target.value)} />
          <input style={EDIT_INPUT} placeholder="Début AAAA-MM-JJ" value={c.start_date || ""} onChange={(e) => setC("start_date", e.target.value)} data-testid="cpadmin-edit-start" />
          <input style={EDIT_INPUT} placeholder="Fin AAAA-MM-JJ" value={c.end_date || ""} onChange={(e) => setC("end_date", e.target.value)} />
          <input style={EDIT_INPUT} placeholder="Formats (ex. 16:9 · 9:16 · 1:1)" value={c.formats || ""} onChange={(e) => setC("formats", e.target.value)} data-testid="cpadmin-edit-formats" />
          <input style={EDIT_INPUT} placeholder="Début diffusion AAAA-MM-JJ" value={c.diffusion_start || ""} onChange={(e) => setC("diffusion_start", e.target.value)} data-testid="cpadmin-edit-diffusion-start" />
        </div>
        <button className="btn btn-primary" style={{ padding: "8px 16px", marginTop: 12 }} onClick={saveCamp} data-testid="cpadmin-edit-save">Enregistrer la campagne</button>
      </Section>

      <Section title="Jalons — Création" testid="cpadmin-edit-ms-creation">
        {dash.milestones.filter((m) => m.phase === "creation").map(msRow)}
        <button className="btn" style={{ padding: "6px 12px", marginTop: 8 }} onClick={() => addMs("creation")} data-testid="cpadmin-addms-creation">+ Ajouter un jalon</button>
      </Section>
      <Section title="Jalons — Production" testid="cpadmin-edit-ms-production">
        {dash.milestones.filter((m) => m.phase === "production").map(msRow)}
        <button className="btn" style={{ padding: "6px 12px", marginTop: 8 }} onClick={() => addMs("production")} data-testid="cpadmin-addms-production">+ Ajouter un jalon</button>
      </Section>

      <Section title="Livrables" testid="cpadmin-edit-deliverables">
        {dash.deliverables.map((x) => (
          <div key={x.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, alignItems: "center", padding: "6px 0" }} data-testid={`cpadmin-deliv-${x.kind}`}>
            <span className="t-label">{x.kind === "storyboard" ? "Storyboard" : "Vidéo"}</span>
            <input style={EDIT_INPUT} placeholder="Statut" value={x.status_label || ""} onChange={(e) => setDl(x.id, "status_label", e.target.value)} data-testid={`cpadmin-deliv-status-${x.kind}`} />
            <input style={EDIT_INPUT} placeholder="Version" value={x.version_label || ""} onChange={(e) => setDl(x.id, "version_label", e.target.value)} />
            <button className="btn" style={{ padding: "5px 8px" }} onClick={() => saveDl(x)} data-testid={`cpadmin-deliv-save-${x.kind}`}>OK</button>
          </div>
        ))}
      </Section>

      <Section title="Diffusion (canaux)" testid="cpadmin-edit-channels">
        {dash.channels.map((ch) => (
          <div key={ch.id} style={{ borderTop: "1px solid var(--li2)", padding: "10px 0" }} data-testid={`cpadmin-channel-${ch.id}`}>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input style={EDIT_INPUT} value={ch.channel_name || ""} onChange={(e) => setCh(ch.id, "channel_name", e.target.value)} data-testid={`cpadmin-channel-name-${ch.id}`} />
              <button className="btn" style={{ padding: "5px 10px" }} onClick={() => saveCh(ch)} data-testid={`cpadmin-channel-save-${ch.id}`}>OK</button>
              <button className="btn" style={{ padding: "5px 10px", color: "#8C1D18" }} onClick={() => delCh(ch.id)} data-testid={`cpadmin-channel-del-${ch.id}`}>Suppr</button>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input style={EDIT_INPUT} type="number" placeholder="Budget €" value={ch.budget_eur || ""} onChange={(e) => setCh(ch.id, "budget_eur", e.target.value)} data-testid={`cpadmin-channel-budget-${ch.id}`} />
              <input style={EDIT_INPUT} placeholder="Estimation (ex. ≈ 1,2 M impressions)" value={ch.estimate_label || ""} onChange={(e) => setCh(ch.id, "estimate_label", e.target.value)} data-testid={`cpadmin-channel-estimate-${ch.id}`} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ display: "grid", gap: 4 }}>
                  <input style={EDIT_INPUT} placeholder={`KPI ${i + 1} libellé`} value={ch.kpis[i]?.label || ""} onChange={(e) => setKpi(ch.id, i, "label", e.target.value)} data-testid={`cpadmin-kpi-label-${ch.id}-${i}`} />
                  <input style={EDIT_INPUT} placeholder="Valeur" value={ch.kpis[i]?.value || ""} onChange={(e) => setKpi(ch.id, i, "value", e.target.value)} data-testid={`cpadmin-kpi-value-${ch.id}-${i}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
        <button className="btn" style={{ padding: "6px 12px", marginTop: 10 }} onClick={addCh} data-testid="cpadmin-addchannel">+ Ajouter un canal</button>
      </Section>

      <Section title="Documents" testid="cpadmin-edit-documents">
        <div
          className="cpadmin-drop"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadDoc(e.dataTransfer.files && e.dataTransfer.files[0]); }}
          style={{ border: `2px dashed ${dragOver ? "#04223F" : "var(--li)"}`, padding: 20, textAlign: "center", background: dragOver ? "#F2F3F5" : "#FAFAFB" }}
          data-testid="cpadmin-doc-dropzone"
        >
          <p className="t-body" style={{ fontSize: 13, margin: "0 0 10px" }}>Glissez un PDF ici (25 Mo max) ou</p>
          <label className="btn btn-primary" style={{ padding: "8px 16px", cursor: "pointer", display: "inline-block" }} data-testid="cpadmin-doc-choose">
            Choisir un fichier
            <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }} onChange={(e) => uploadDoc(e.target.files && e.target.files[0])} data-testid="cpadmin-doc-input" />
          </label>
          {upPct !== null && <p className="t-body" style={{ fontSize: 13, marginTop: 10 }} data-testid="cpadmin-doc-progress">Envoi… {upPct}%</p>}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 2fr auto", gap: 8, alignItems: "center", marginTop: 14 }}>
          <select style={EDIT_INPUT} value={docForm.kind} onChange={(e) => setDocForm({ ...docForm, kind: e.target.value })} data-testid="cpadmin-doc-kind">
            <option value="brief">Brief</option><option value="devis">Devis</option><option value="facture">Facture</option><option value="contrat">Contrat</option><option value="autre">Autre</option>
          </select>
          <input style={EDIT_INPUT} placeholder="Titre (sinon nom du fichier)" value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} data-testid="cpadmin-doc-title" />
          <input style={EDIT_INPUT} placeholder="Ligne méta (ex. Signé · 18 500 € HT)" value={docForm.meta} onChange={(e) => setDocForm({ ...docForm, meta: e.target.value })} data-testid="cpadmin-doc-meta" />
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={docForm.published} onChange={(e) => setDocForm({ ...docForm, published: e.target.checked })} data-testid="cpadmin-doc-published" />
            Visible par le client
          </label>
        </div>
        <p className="t-body" style={{ fontSize: 12, color: "var(--roi)", marginTop: 6 }}>Le type, le titre, la méta et la visibilité s'appliquent au prochain fichier déposé.</p>

        <div style={{ marginTop: 16 }} data-testid="cpadmin-doc-list">
          {docs.length === 0 && <p className="t-body" style={{ fontSize: 14 }}>Aucun document.</p>}
          {docs.map((doc) => (
            <div key={doc.id} style={{ borderTop: "1px solid var(--li2)", padding: "10px 0" }} data-testid={`cpadmin-doc-${doc.id}`}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 2fr", gap: 8, marginBottom: 6 }}>
                <select style={EDIT_INPUT} value={doc.kind} onChange={(e) => setDoc(doc.id, "kind", e.target.value)}>
                  <option value="brief">Brief</option><option value="devis">Devis</option><option value="facture">Facture</option><option value="contrat">Contrat</option><option value="autre">Autre</option>
                </select>
                <input style={EDIT_INPUT} value={doc.title || ""} onChange={(e) => setDoc(doc.id, "title", e.target.value)} data-testid={`cpadmin-doc-title-${doc.id}`} />
                <input style={EDIT_INPUT} value={doc.meta || ""} onChange={(e) => setDoc(doc.id, "meta", e.target.value)} data-testid={`cpadmin-doc-meta-${doc.id}`} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span className="t-body" style={{ fontSize: 12.5, color: "var(--roi)" }}>{doc.original_filename} · {humanSize(doc.size_bytes)} · {doc.published ? "publié" : "brouillon"}</span>
                <span style={{ flex: 1 }} />
                <button className="btn" style={{ padding: "5px 10px" }} onClick={() => saveDocMeta(doc)} data-testid={`cpadmin-doc-save-${doc.id}`}>OK</button>
                <button className="btn" style={{ padding: "5px 10px" }} onClick={() => togglePublish(doc)} data-testid={`cpadmin-doc-publish-${doc.id}`}>{doc.published ? "Dépublier" : "Publier"}</button>
                <button className="btn" style={{ padding: "5px 10px", color: "#8C1D18" }} onClick={() => delDoc(doc)} data-testid={`cpadmin-doc-del-${doc.id}`}>Suppr</button>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function CampaignPreview({ campaignId }) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => setData(await cpGet(`/campaigns/${campaignId}/dashboard`)))(); }, [campaignId]);
  const openAdminDoc = async (id, download) => {
    try {
      const r = await fetch(`${CP}/documents/${id}/file${download ? "?download=1" : ""}`, { headers: adminHeaders() });
      if (!r.ok) return;
      const url = URL.createObjectURL(await r.blob());
      if (download) {
        const a = document.createElement("a");
        a.href = url; a.download = "document.pdf"; document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (_) { /* silencieux */ }
  };
  return (
    <div data-testid="cpadmin-preview">
      <div className="cp-preview-banner" data-testid="cpadmin-preview-banner">Aperçu admin — vue client en lecture seule</div>
      <div className="cp-root" style={{ background: "transparent" }}>
        <div className="cp-wrap">
          {data ? <CampaignDashboardView data={data} onOpenDocument={openAdminDoc} /> : <p className="cp-muted">Chargement…</p>}
        </div>
      </div>
    </div>
  );
}

export default function AdminCampagnes() {
  useNoindex();
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("sliz_admin_token") || "");
  // Aucun jeton admin en localStorage -> on renvoie vers le vrai back-office /admin.
  useEffect(() => {
    if (!token) navigate("/admin", { replace: true });
  }, [token, navigate]);

  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [resetReqs, setResetReqs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [clientAccounts, setClientAccounts] = useState([]);

  const [orgName, setOrgName] = useState("");
  const [u, setU] = useState({ email: "", first_name: "", last_name: "", role: "client_valideur", organization_id: "" });
  const [camp, setCamp] = useState({ organization_id: "", name: "", advertiser_label: "", status: "active", start_date: "", end_date: "", owner_name: "" });

  const [link, setLink] = useState("");
  const [seedCreds, setSeedCreds] = useState([]);
  const [seedEnabled, setSeedEnabled] = useState(false);
  const [msg, setMsg] = useState("");
  const [editId, setEditId] = useState(null);
  const [preview, setPreview] = useState(false);

  const reload = async () => {
    if (!token) return;
    setOrgs(await cpGet("/organizations"));
    setUsers(await cpGet("/users"));
    setCampaigns(await cpGet("/campaigns"));
    setResetReqs(await cpGet("/reset-requests"));
    setAudit(await cpGet("/audit-log"));
    setClientAccounts(await cpGet("/client-accounts"));
    const st = await cpGet("/seed-status");
    setSeedEnabled(!!(st && st.enabled));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [token]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 3000); };
  const orgName_ = (id) => (orgs.find((o) => o.id === id) || {}).name || id;

  const createOrg = async () => {
    if (!orgName.trim()) return;
    const r = await cpPost("/organizations", { name: orgName.trim() });
    if (r.ok) { setOrgName(""); flash("Organisation créée"); reload(); }
    else flash(r.data.detail || "Erreur");
  };
  const createUser = async () => {
    const r = await cpPost("/users", u);
    if (r.ok) { flash("Compte client créé (invité)"); setU({ ...u, email: "", first_name: "", last_name: "" }); reload(); }
    else flash(r.data.detail || "Erreur");
  };
  const createCamp = async () => {
    if (!camp.organization_id) { flash("Choisissez une organisation cliente."); return; }
    const r = await cpPost("/campaigns", camp);
    if (r.ok) { flash("Campagne créée"); setCamp({ ...camp, name: "", advertiser_label: "", start_date: "", end_date: "", owner_name: "" }); reload(); }
    else flash(r.data.detail || "Erreur");
  };
  const genInvite = async (userId, purpose) => {
    const r = await cpPost("/invitations", { user_id: userId, purpose });
    if (r.ok) { setLink(r.data.link); flash("Lien généré (copiez-le)"); }
    else flash(r.data.detail || "Erreur");
  };
  const disableUser = async (userId) => {
    if (!window.confirm("Désactiver ce compte client ?")) return;
    const r = await cpPost(`/users/${userId}/disable`);
    if (r.ok) { flash("Compte désactivé"); reload(); }
  };
  const genResetLink = async (reqId) => {
    const r = await cpPost(`/reset-requests/${reqId}/generate-link`);
    if (r.ok) { setLink(r.data.link); flash("Lien de réinitialisation généré"); reload(); }
    else flash(r.data.detail || "Erreur");
  };
  const seed = async () => {
    const r = await cpPost("/seed-test");
    if (r.ok) { setSeedCreds(r.data.credentials || []); flash(r.data.note || "Seed de test exécuté"); reload(); }
    else flash(r.data.detail || "Seed refusé");
  };
  const seedRegenerate = async () => {
    if (!window.confirm("Régénérer les mots de passe des comptes de test ? Les anciens seront invalidés.")) return;
    const r = await cpPost("/seed-test?regenerate=true");
    if (r.ok) { setSeedCreds(r.data.credentials || []); flash("Mots de passe régénérés"); reload(); }
    else flash(r.data.detail || "Seed refusé");
  };
  const unblockUser = async (userId) => {
    const r = await cpPost(`/users/${userId}/unblock`);
    if (r.ok) { flash("Compte débloqué"); reload(); }
    else flash(r.data.detail || "Erreur");
  };

  if (!token) return null;

  if (editId) {
    return (
      <main style={{ minHeight: "100vh", background: "var(--pp2)" }} data-testid="cpadmin-panel">
        <div style={{ background: "#fff", borderBottom: "1px solid var(--li)" }}>
          <div className="wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
            <span className="t-logo" style={{ fontSize: 16 }}>SLI<span style={{ color: "var(--roi)" }}>Z</span> — {preview ? "Aperçu campagne" : "Édition campagne"}</span>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              {!preview && <button className="btn" style={{ padding: "6px 12px" }} onClick={() => setPreview(true)} data-testid="cpadmin-view-as-client">Voir comme le client</button>}
              {preview && <button className="btn" style={{ padding: "6px 12px" }} onClick={() => setPreview(false)} data-testid="cpadmin-preview-back">← Édition</button>}
              <button className="t-label" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => { setEditId(null); setPreview(false); reload(); }} data-testid="cpadmin-editor-close">← Campagnes</button>
            </div>
          </div>
        </div>
        <div className="wrap" style={{ paddingTop: preview ? 0 : 32, paddingBottom: 80, maxWidth: 1000 }}>
          {msg && <p className="t-label" style={{ color: "var(--roi)" }} data-testid="cpadmin-flash">{msg}</p>}
          {preview ? <CampaignPreview campaignId={editId} /> : <CampaignEditor campaignId={editId} flash={flash} />}
        </div>
      </main>
    );
  }

  const inputStyle = { fontFamily: "Archivo", fontSize: 14, padding: "9px 11px", border: "1px solid var(--li)", width: "100%" };

  return (
    <main style={{ minHeight: "100vh", background: "var(--pp2)" }} data-testid="cpadmin-panel">
      <div style={{ background: "#fff", borderBottom: "1px solid var(--li)" }}>
        <div className="wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <span className="t-logo" style={{ fontSize: 16 }}>SLI<span style={{ color: "var(--roi)" }}>Z</span> — Studio campagnes</span>
          <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
            <a href="/admin" className="t-label" data-testid="cpadmin-back">Back-office</a>
            <button className="t-label" style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="cpadmin-logout"
              onClick={() => { localStorage.removeItem("sliz_admin_token"); setToken(""); }}>Déconnexion</button>
          </div>
        </div>
      </div>

      <div className="wrap" style={{ paddingTop: 32, paddingBottom: 80, maxWidth: 900 }}>
        {msg && <p className="t-label" style={{ color: "var(--roi)" }} data-testid="cpadmin-flash">{msg}</p>}

        {link && (
          <div style={{ background: "#0b1220", color: "#fff", padding: 16, marginBottom: 24, wordBreak: "break-all" }} data-testid="cpadmin-link-box">
            <p className="t-label" style={{ color: "#22C55E", margin: "0 0 6px" }}>Lien à envoyer (usage unique)</p>
            <code style={{ fontSize: 13 }} data-testid="cpadmin-link-value">{link}</code>
            <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
              <button className="btn" style={{ padding: "6px 12px" }} data-testid="cpadmin-link-copy"
                onClick={() => { navigator.clipboard?.writeText(link); flash("Lien copié"); }}>Copier</button>
              <button className="btn" style={{ padding: "6px 12px" }} onClick={() => setLink("")}>Fermer</button>
            </div>
          </div>
        )}

        <Section title="Organisation (annonceur)" testid="cpadmin-orgs">
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <input style={{ ...inputStyle, maxWidth: 320 }} placeholder="Nom de l'organisation" value={orgName}
              onChange={(e) => setOrgName(e.target.value)} data-testid="cpadmin-org-name" />
            <button className="btn btn-primary" style={{ padding: "8px 16px" }} onClick={createOrg} data-testid="cpadmin-org-create">Créer</button>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }} data-testid="cpadmin-org-list">
            {orgs.map((o) => <li key={o.id}>{o.name}</li>)}
          </ul>
        </Section>

        <Section title="Compte client" testid="cpadmin-users">
          <div className="grid gap-3 md:grid-cols-2" style={{ marginBottom: 16 }}>
            <input style={inputStyle} placeholder="E-mail" value={u.email} onChange={(e) => setU({ ...u, email: e.target.value })} data-testid="cpadmin-user-email" />
            <select style={inputStyle} value={u.organization_id} onChange={(e) => setU({ ...u, organization_id: e.target.value })} data-testid="cpadmin-user-org">
              <option value="">— Organisation —</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <input style={inputStyle} placeholder="Prénom" value={u.first_name} onChange={(e) => setU({ ...u, first_name: e.target.value })} data-testid="cpadmin-user-first" />
            <input style={inputStyle} placeholder="Nom" value={u.last_name} onChange={(e) => setU({ ...u, last_name: e.target.value })} data-testid="cpadmin-user-last" />
            <select style={inputStyle} value={u.role} onChange={(e) => setU({ ...u, role: e.target.value })} data-testid="cpadmin-user-role">
              <option value="client_valideur">client_valideur</option>
              <option value="client_lecteur">client_lecteur</option>
            </select>
            <button className="btn btn-primary" style={{ padding: "8px 16px" }} onClick={createUser} data-testid="cpadmin-user-create">Créer le compte</button>
          </div>
          <div style={{ borderTop: "1px solid var(--li)" }} data-testid="cpadmin-user-list">
            {users.map((usr) => (
              <div key={usr.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid var(--li2)", fontSize: 14, gap: 12, flexWrap: "wrap" }} data-testid={`cpadmin-user-${usr.id}`}>
                <span>{usr.email} · <span className="t-label">{orgName_(usr.organization_id)}</span> · {usr.role} · <em>{usr.status}</em></span>
                <span style={{ display: "flex", gap: 8 }}>
                  <button className="btn" style={{ padding: "5px 10px" }} onClick={() => genInvite(usr.id, "invite")} data-testid={`cpadmin-invite-${usr.id}`}>Lien invitation</button>
                  <button className="btn" style={{ padding: "5px 10px" }} onClick={() => genInvite(usr.id, "reset")} data-testid={`cpadmin-reset-${usr.id}`}>Lien reset</button>
                  {usr.status !== "disabled" && <button className="btn" style={{ padding: "5px 10px", color: "#8C1D18" }} onClick={() => disableUser(usr.id)} data-testid={`cpadmin-disable-${usr.id}`}>Désactiver</button>}
                </span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Comptes clients" testid="cpadmin-accounts">
          {clientAccounts.length === 0 && <p className="t-body" style={{ fontSize: 14 }}>Aucun compte client.</p>}
          <div data-testid="cpadmin-accounts-list">
            {clientAccounts.map((acc) => {
              const locked = isLocked(acc);
              return (
                <div key={acc.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: "1px solid var(--li2)", fontSize: 14, gap: 12, flexWrap: "wrap" }} data-testid={`cpadmin-account-${acc.id}`}>
                  <span>
                    {acc.email} · <span className="t-label">{acc.organization_name || "—"}</span> · <em>{STATUS_FR[acc.status] || acc.status}</em>
                    {" · "}Dernière connexion : {acc.last_login_at ? new Date(acc.last_login_at).toLocaleString("fr-FR") : "jamais"}
                    {locked && (
                      <span style={{ color: "#8C1D18", fontWeight: 600 }} data-testid={`cpadmin-account-locked-${acc.id}`}>
                        {" · "}Bloqué jusqu'à {frTime(acc.locked_until)}
                      </span>
                    )}
                  </span>
                  {locked && (
                    <button className="btn" style={{ padding: "5px 10px" }} onClick={() => unblockUser(acc.id)} data-testid={`cpadmin-account-unblock-${acc.id}`}>Débloquer</button>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        <Section title="Campagne" testid="cpadmin-campaigns">
          <div className="grid gap-3 md:grid-cols-2" style={{ marginBottom: 16 }}>
            <select style={{ ...inputStyle, borderColor: camp.organization_id ? undefined : "#8C1D18" }} value={camp.organization_id} onChange={(e) => setCamp({ ...camp, organization_id: e.target.value })} data-testid="cpadmin-camp-org">
              <option value="">— Organisation cliente (obligatoire) —</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <input style={inputStyle} placeholder="Nom de la campagne" value={camp.name} onChange={(e) => setCamp({ ...camp, name: e.target.value })} data-testid="cpadmin-camp-name" />
            <input style={inputStyle} placeholder="Libellé annonceur" value={camp.advertiser_label} onChange={(e) => setCamp({ ...camp, advertiser_label: e.target.value })} data-testid="cpadmin-camp-label" />
            <input style={inputStyle} placeholder="Statut" value={camp.status} onChange={(e) => setCamp({ ...camp, status: e.target.value })} data-testid="cpadmin-camp-status" />
            <input style={inputStyle} placeholder="Début (AAAA-MM-JJ)" value={camp.start_date} onChange={(e) => setCamp({ ...camp, start_date: e.target.value })} data-testid="cpadmin-camp-start" />
            <input style={inputStyle} placeholder="Fin (AAAA-MM-JJ)" value={camp.end_date} onChange={(e) => setCamp({ ...camp, end_date: e.target.value })} data-testid="cpadmin-camp-end" />
            <button className="btn btn-primary" style={{ padding: "8px 16px", opacity: camp.organization_id ? 1 : 0.5 }} disabled={!camp.organization_id} onClick={createCamp} data-testid="cpadmin-camp-create">Créer la campagne</button>
          </div>
          <div style={{ fontSize: 14 }} data-testid="cpadmin-camp-list">
            {campaigns.map((c) => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--li2)" }}>
                <span>{c.name} · <span className="t-label">{orgName_(c.organization_id)}</span> · {frDate(c.start_date)} → {frDate(c.end_date)} · {c.status_label || c.status}</span>
                <button className="btn" style={{ padding: "5px 10px" }} onClick={() => { setEditId(c.id); setPreview(false); }} data-testid={`cpadmin-edit-${c.id}`}>Éditer</button>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Demandes de réinitialisation" testid="cpadmin-resets">
          {resetReqs.length === 0 && <p className="t-body" style={{ fontSize: 14 }}>Aucune demande en attente.</p>}
          {resetReqs.map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: "1px solid var(--li2)", fontSize: 14 }} data-testid={`cpadmin-resetreq-${r.id}`}>
              <span>{r.email} · <span className="t-label">{orgName_(r.organization_id)}</span> · {r.created_at ? new Date(r.created_at).toLocaleString("fr-FR") : ""} · IP {r.ip || "—"}</span>
              <button className="btn" style={{ padding: "5px 10px" }} onClick={() => genResetLink(r.id)} data-testid={`cpadmin-resetreq-gen-${r.id}`}>Générer le lien</button>
            </div>
          ))}
        </Section>

        <Section title="Journal d'audit" testid="cpadmin-audit">
          <div style={{ maxHeight: 300, overflow: "auto", fontSize: 13 }} data-testid="cpadmin-audit-list">
            {audit.map((a) => (
              <div key={a.id} style={{ padding: "6px 0", borderTop: "1px solid var(--li2)" }}>
                <span className="t-label">{a.created_at ? new Date(a.created_at).toLocaleString("fr-FR") : ""}</span> · {a.actor_type}:{a.actor_id || "—"} · <strong>{a.action}</strong> · {a.target_type}
              </div>
            ))}
          </div>
        </Section>

        {seedEnabled && (
        <Section title="Seed de test (preview uniquement)" testid="cpadmin-seed">
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn" style={{ padding: "8px 16px" }} onClick={seed} data-testid="cpadmin-seed-run">Exécuter le seed de test</button>
            <button className="btn" style={{ padding: "8px 16px", color: "#8C1D18" }} onClick={seedRegenerate} data-testid="cpadmin-seed-regenerate">Régénérer les mots de passe de test</button>
          </div>
          <p className="t-body" style={{ fontSize: 13, marginTop: 10, color: "var(--roi)" }}>
            Le seed crée/rafraîchit les données de test <strong>sans changer les mots de passe existants</strong>. Utilisez « Régénérer » pour en générer de nouveaux (les anciens seront invalidés).
          </p>
          {seedCreds.length > 0 && (
            <div style={{ background: "#0b1220", color: "#fff", padding: 16, marginTop: 16 }} data-testid="cpadmin-seed-creds">
              <p className="t-label" style={{ color: "#22C55E", margin: "0 0 8px" }}>Identifiants (mots de passe affichés une seule fois)</p>
              {seedCreds.map((c) => (
                <div key={c.email} style={{ fontSize: 13, marginBottom: 4 }}>
                  <strong>{c.organization}</strong> — {c.email} / {c.password
                    ? <code>{c.password}</code>
                    : <em style={{ opacity: 0.7 }}>mot de passe inchangé</em>}
                </div>
              ))}
            </div>
          )}
        </Section>
        )}
      </div>
    </main>
  );
}
